import { useState } from 'react';
import axios from 'axios';

import { makeSpaceBetweenSpots } from './utils/map';


type SearchArguments = {
    genre: string;
    keyword: string;
    area: string;
    lat?: number;
    lng?: number;
};

type RegisterArguments = {
    id?: number;
    name: string;
    genre: string;
    pref: string;
    city: string;
    address: string;
    description: string;
    opening_hours: string;
    member_benefits: string;
    lat: number;
    lng: number;
    cover_image: string;
    delivery: boolean;
    takeout: boolean;
    lunch: boolean;
    dinner: boolean;
    alcohol: boolean;
    cafe: boolean;
    power_supply: boolean;
    manual_input: boolean;
};

type SpotApiResponse = {
    spot: {
        id: number;
        genre: string;
        parent_genre: string;
        name: string;
        pref: string;
        city: string;
        address: string;
        description: string;
        opening_hours: string;
        member_benefits: string;
        lat: number;
        lng: number;
        cover_image: string;
        manual_input: boolean;
        delivery: boolean;
        takeout: boolean;
        lunch: boolean;
        dinner: boolean;
        alcohol: boolean;
        cafe: boolean;
        chimney_coffee: boolean;
        power_supply: boolean;
        has_valid_coupons: boolean;
        user: {
            id: string;
            facebook_name: string;
            facebook_user_id: string;
            short_id: string;
            icon_path: string;
        };
        staffs?: {
            id: number;
            userId: string;
            name: string;
            role: string;
            facebookId: string;
            shortId: string;
            public: boolean;
            icon_path: string;
        }[];
    };
    editable: boolean;
    checkedIn: boolean;
    public?: boolean;
    role?: string;
    requestedDate?: string;
};

type SpotsApiResponse = {
    spots: {
        id: number;
        genre: string;
        parent_genre: string;
        name: string;
        pref: string;
        city: string;
        address: string;
        description: string;
        opening_hours: string;
        member_benefits: string;
        lat: number;
        lng: number;
        cover_image: string;
        manual_input: boolean;
        delivery: boolean;
        takeout: boolean;
        lunch: boolean;
        dinner: boolean;
        alcohol: boolean;
        cafe: boolean;
        chimney_coffee: boolean;
        power_supply: boolean;
        has_valid_coupons: boolean;
        user: {
            id: string;
            facebook_name: string;
            facebook_user_id: string;
            icon_path: string;
        };
    }[];
};


export function useSpots() {
    const [loading, setLoading] = useState(false);

    async function all(currentLocation: { lat: number, lng: number }) {
        setLoading(!loading);
        try {
            const { data } = await axios.get<SpotsApiResponse>(
                `${window.location.origin}/api/map/spots`,
                {
                    params: { ...currentLocation },
                },
            );
            const result: Spots = data.spots?.map((spot) => {
                return {
                    ...spot,
                    memberBenefits: spot.member_benefits ?? null,
                    chimneyCoffee: spot.chimney_coffee ?? false,
                    powerSupply: spot.power_supply ?? false,
                    openingHours: spot.opening_hours,
                    contributor: spot.user.facebook_name,
                    userId: spot.user.id,
                    facebookId: spot.user.facebook_user_id,
                    parentGenre: spot.parent_genre,
                    coverImage: spot.cover_image,
                    icon_path: spot.user.icon_path,
                    hasValidCoupons: spot.has_valid_coupons ?? false,
                };
            }) ?? [];
            return makeSpaceBetweenSpots(result);
        } catch (e) {
            return [];
        } finally {
            setLoading(!loading);
        };
    };

    async function get(id: number): Promise<Spot | null> {
        setLoading(!loading);
        try {
            const { data: { spot, editable, checkedIn, role, requestedDate } } = await axios.get<SpotApiResponse>(`${window.location.origin}/api/map/spots/${id}`);
            if (spot) {
                return {
                    ...spot,
                    manualInput: spot.manual_input,
                    memberBenefits: spot.member_benefits ?? null,
                    chimneyCoffee: spot.chimney_coffee ?? false,
                    powerSupply: spot.power_supply ?? false,
                    openingHours: spot.opening_hours,
                    contributor: spot.user.facebook_name,
                    userId: spot.user.id,
                    facebookId: spot.user.facebook_user_id,
                    shortId: spot.user.short_id,
                    parentGenre: spot.parent_genre,
                    editable: editable,
                    checkedIn: checkedIn,
                    coverImage: spot.cover_image,
                    staffs: spot.staffs ?? [],
                    role,
                    requestedDate,
                    icon_path: spot.user.icon_path,
                    hasValidCoupons: spot.has_valid_coupons ?? false,
                };
            } else {
                return null;
            }
        } catch (e) {
            throw e;
        } finally {
            setLoading(!loading);
        };
    };

    async function register(args: RegisterArguments) {
        await axios.get(`${window.location.origin}/api/isActiveSalonMember`);
        setLoading(true);
        try {
            const { data: { id, errors }, status } = await axios.post(
                `${window.location.origin}/api/map/spots`,
                args,
            );
            return { id, error: status !== 200, errors };
        } finally {
            setLoading(false);
        };
    };

    async function erase(id: number) {
        setLoading(!loading);
        try {
            const { status } = await axios.delete(`${window.location.origin}/api/map/spots/${id}`);
            return { error: status !== 200 };
        } finally {
            setLoading(!loading);
        };
    };

    async function edit(form: RegisterArguments) {
        await axios.get(`${window.location.origin}/api/isActiveSalonMember`);
        setLoading(true);
        try {
            const { data: { errors }, status } = await axios.put(
                `${window.location.origin}/api/map/spots`,
                form,
            );
            return { error: status !== 200, errors };
        } finally {
            setLoading(false);
        };
    };

    async function search(args: SearchArguments) {
        setLoading(!loading);
        try {
            const { data } = await axios.get<SpotsApiResponse>(
                `${window.location.origin}/api/map/spots`,
                {
                    params: {
                        genre: args.genre !== '' ? args.genre : null,
                        keyword: args.keyword !== '' ? args.keyword : null,
                        area: args.area !== '' ? args.area : null,
                        lat: args.lat !== null ? args.lat : null,
                        lng: args.lng !== null ? args.lng : null,
                    },
                },
            );
            const result: Spots = data.spots?.map((spot) => {
                return {
                    ...spot,
                    memberBenefits: spot.member_benefits ?? null,
                    chimneyCoffee: spot.chimney_coffee ?? false,
                    powerSupply: spot.power_supply ?? false,
                    openingHours: spot.opening_hours,
                    contributor: spot.user.facebook_name,
                    userId: spot.user.id,
                    facebookId: spot.user.facebook_user_id,
                    parentGenre: spot.parent_genre,
                    coverImage: spot.cover_image,
                    icon_path: spot.user.icon_path,
                    hasValidCoupons: spot.has_valid_coupons ?? false,
                }
            }) ?? [];
            return makeSpaceBetweenSpots(result);
        } catch (e) {
            throw e;
        } finally {
            setLoading(!loading);
        };
    };

    return { all, get, search, register, edit, erase, loading };
};
